import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import { ConnectAccountContent } from '../components/connect-account/ConnectAccountContent';
import {
  useAcceptInvitation,
  useGetAllCountries,
  useGetAllCountriesPhoenix,
  useGetNursingHomeList,
  useRejectInvitation,
  useServiceProvider,
  useUpdateServiceProvider,
  useUpdateUserProfile,
  useUserProfile,
} from '@pflegenavi/service-provider-frontend/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import type { IConnectAccountFormValues } from '../components/connect-account/ConnectForm';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { sanitizeString } from '@pflegenavi/shared/utils';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Stack } from '@mui/material';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag } from '@pflegenavi/shared/api';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface NewInvitationPageProps {}

// eslint-disable-next-line complexity
export const ConnectAccountPage: FC<NewInvitationPageProps> = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenant');
  const nursingHomeId = searchParams.get('nursingHomeId');
  const globalServiceProviderId = searchParams.get('globalServiceProviderId');
  const { user } = useAuthentication();
  const { mutateAsync: acceptInvitation, isLoading: acceptInvitationLoading } =
    useAcceptInvitation();
  const { mutateAsync: rejectInvitation, isLoading: rejectInvitationLoading } =
    useRejectInvitation();
  const {
    mutateAsync: updateUserProfile,
    isLoading: updateUserProfileLoading,
  } = useUpdateUserProfile();
  const {
    mutateAsync: updateServiceProvider,
    isLoading: updateServiceProviderLoading,
  } = useUpdateServiceProvider();
  const { data: nursingHomeList, isLoading: isNursingHomeListLoading } =
    useGetNursingHomeList();
  const { data: userProfile, isLoading: isUserProfileLoading } = useUserProfile(
    {}
  );

  const userId = user?.userId;

  if (!tenant || !globalServiceProviderId || !nursingHomeId) {
    throw new Error('Missing required parameters for invitation');
  }

  const { data: serviceProvider, isLoading: isServiceProviderLoading } =
    useServiceProvider(
      {
        globalServiceProviderId: globalServiceProviderId,
      },
      {
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      }
    );

  const usePhoenix = useFeatureFlagEnabled(FeatureFlag.dev_ReceiptBatchPhoenix);
  const { data: countries, isLoading: areCountriesLoading } = (
    usePhoenix ? useGetAllCountriesPhoenix : useGetAllCountries
  )({
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  // The nursing home is not there if a) the nursing home does not exist or b) the invitation is not valid anymore
  const nursingHome = useMemo(() => {
    return nursingHomeList?.find(
      (nursingHome) => nursingHome.nursingHomeId === nursingHomeId
    );
  }, [nursingHomeList, nursingHomeId]);

  const userDetails = useMemo(() => {
    return {
      firstName:
        userProfile?.user.firstName &&
        userProfile.user.firstName !== userProfile.user.eMail // The endpoint returns the email address also in the firstName field if no firstName is set
          ? userProfile.user.firstName
          : '',
      lastName: userProfile?.user.lastName ?? '',
    };
  }, [userProfile]);

  // eslint-disable-next-line complexity
  const companyDetails = useMemo(() => {
    return {
      name: serviceProvider?.name ?? '',
      address: serviceProvider?.address ?? '',
      city: serviceProvider?.city ?? '',
      zipCode: serviceProvider?.zipCode ?? '',
      phone: serviceProvider?.phone ?? '',
      countryId: serviceProvider?.countryId ?? '',
    };
  }, [serviceProvider]);

  const onInvitationResponse = useCallback(
    async (
      values: IConnectAccountFormValues,
      responseToInvitation?: boolean
    ) => {
      if (!userId) {
        throw new Error('Id of logged in user could not be obtained');
      }

      await updateUserProfile({
        id: userId,
        firstName: sanitizeString(values.firstName),
        lastName: sanitizeString(values.lastName),
      });

      await updateServiceProvider({
        globalServiceProviderId: globalServiceProviderId,
        data: {
          name: sanitizeString(values.companyName),
          address: sanitizeString(values.address),
          city: sanitizeString(values.city),
          zipCode: sanitizeString(values.zipCode),
          phone: sanitizeString(values.phone),
          countryId: values.countryId,
        },
      });

      if (nursingHome && responseToInvitation !== undefined) {
        if (responseToInvitation) {
          await acceptInvitation({
            nursingHomeId: nursingHome.nursingHomeId,
            globalServiceProviderId,
            tenant,
          });
          enqueueSnackbar(
            t('service-provider-app.connect-account.invitation-accepted'),
            { variant: 'success' }
          );
        } else {
          await rejectInvitation({
            nursingHomeId: nursingHome.nursingHomeId,
            globalServiceProviderId,
            tenant,
          });
          enqueueSnackbar(
            t('service-provider-app.connect-account.invitation-rejected'),
            { variant: 'info' }
          );
        }
      } else {
        // If the nursing home is not there, it either does not exist (unlikely) or the invitation is not valid anymore.
        // In that case, the user can still set up their account details, but is not affiliated with a nursing home (but
        // may be invited by one later on).
        enqueueSnackbar(t('service-provider-app.connect-account.data-saved'), {
          variant: 'success',
        });
      }

      navigate(spAppMainPages.HOME);
    },
    [
      rejectInvitation,
      enqueueSnackbar,
      t,
      updateServiceProvider,
      userId,
      updateUserProfile,
      acceptInvitation,
      globalServiceProviderId,
      navigate,
      nursingHome,
      tenant,
    ]
  );

  if (
    isUserProfileLoading ||
    isNursingHomeListLoading ||
    isServiceProviderLoading ||
    areCountriesLoading
  ) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          height: '100%',
          overflow: 'hidden',
          width: '100%',
          display: 'flex',
        }}
      >
        <CircularProgress color="primary" variant="indeterminate" />
      </Stack>
    );
  }

  return (
    <ConnectAccountContent
      userDetails={userDetails}
      companyDetails={companyDetails}
      countryOptions={countries ?? []}
      loading={
        isUserProfileLoading ||
        isNursingHomeListLoading ||
        isServiceProviderLoading ||
        areCountriesLoading ||
        acceptInvitationLoading ||
        rejectInvitationLoading ||
        updateUserProfileLoading ||
        updateServiceProviderLoading
      }
      accountAffiliationName={nursingHome?.nursingHomeName}
      connectionStatus={nursingHome?.connectionStatus}
      handleConnectAccount={onInvitationResponse}
    />
  );
};
